import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function factorial(n: number): number {
    if (n < 0) throw new FormulaError(FormulaErrorType.NUM, 'FACT requires non-negative integer');
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) result *= i;
    return result;
}

function factorialDouble(n: number): number {
    if (n < -1) throw new FormulaError(FormulaErrorType.NUM, 'FACTDOUBLE requires n >= -1');
    if (n <= 0) return 1;
    let result = 1;
    for (let i = n; i >= 2; i -= 2) result *= i;
    return result;
}

function gcd2(a: number, b: number): number {
    a = Math.abs(Math.trunc(a));
    b = Math.abs(Math.trunc(b));
    while (b !== 0) {
        const t = b;
        b = a % b;
        a = t;
    }
    return a;
}

function lcm2(a: number, b: number): number {
    a = Math.abs(Math.trunc(a));
    b = Math.abs(Math.trunc(b));
    if (a === 0 || b === 0) return 0;
    return (a / gcd2(a, b)) * b;
}

const ROMAN_VALUES: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
];

function toRoman(n: number): string {
    if (n <= 0 || n >= 4000) throw new FormulaError(FormulaErrorType.VALUE, 'ROMAN requires 1–3999');
    let result = '';
    for (const [val, sym] of ROMAN_VALUES) {
        while (n >= val) {
            result += sym;
            n -= val;
        }
    }
    return result;
}

function fromRoman(text: string): number {
    const map: Record<string, number> = { M: 1000, D: 500, C: 100, L: 50, X: 10, V: 5, I: 1 };
    const s = text.toUpperCase().trim();
    let result = 0;
    for (let i = 0; i < s.length; i++) {
        const cur = map[s[i]];
        const nxt = map[s[i + 1]];
        if (cur === undefined) throw new FormulaError(FormulaErrorType.VALUE, `Invalid roman numeral: ${text}`);
        if (nxt !== undefined && cur < nxt) {
            result += nxt - cur;
            i++;
        } else {
            result += cur;
        }
    }
    return result;
}

function subtotalOp(funcNum: number, nums: number[]): number {
    const n = nums.length;
    switch (funcNum) {
        case 1: // AVERAGE
            return n === 0 ? 0 : smartRound(nums.reduce((a, b) => a + b, 0) / n);
        case 2: // COUNT
            return n;
        case 3: // COUNTA – treat every element as non-empty
            return n;
        case 4: // MAX
            return n === 0 ? 0 : Math.max(...nums);
        case 5: // MIN
            return n === 0 ? 0 : Math.min(...nums);
        case 6: // PRODUCT
            return nums.reduce((a, b) => smartRound(a * b), 1);
        case 7: { // STDEV (sample)
            if (n < 2) return 0;
            const mean = nums.reduce((a, b) => a + b, 0) / n;
            const variance = nums.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1);
            return Math.sqrt(variance);
        }
        case 8: { // STDEVP (population)
            if (n === 0) return 0;
            const mean = nums.reduce((a, b) => a + b, 0) / n;
            const variance = nums.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
            return Math.sqrt(variance);
        }
        case 9: // SUM
            return smartRound(nums.reduce((a, b) => a + b, 0));
        case 10: { // VAR (sample)
            if (n < 2) return 0;
            const mean = nums.reduce((a, b) => a + b, 0) / n;
            return nums.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1);
        }
        case 11: { // VARP (population)
            if (n === 0) return 0;
            const mean = nums.reduce((a, b) => a + b, 0) / n;
            return nums.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
        }
        default:
            throw new FormulaError(FormulaErrorType.VALUE, `SUBTOTAL: unknown function_num ${funcNum}`);
    }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export function registerMathFunctions(registry: FunctionRegistry): void {
    // --- Trigonometry ---

    registry.register('ABS', (...args: FormulaValue[]) => Math.abs(toNum(args[0])));

    registry.register('ACOS', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v < -1 || v > 1) throw new FormulaError(FormulaErrorType.NUM, 'ACOS argument out of range');
        return Math.acos(v);
    });

    registry.register('ACOSH', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v < 1) throw new FormulaError(FormulaErrorType.NUM, 'ACOSH argument must be >= 1');
        return Math.acosh(v);
    });

    registry.register('ACOT', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        return Math.PI / 2 - Math.atan(v);
    });

    registry.register('ACOTH', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (Math.abs(v) <= 1) throw new FormulaError(FormulaErrorType.NUM, 'ACOTH argument must have |x| > 1');
        return 0.5 * Math.log((v + 1) / (v - 1));
    });

    registry.register('ASIN', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v < -1 || v > 1) throw new FormulaError(FormulaErrorType.NUM, 'ASIN argument out of range');
        return Math.asin(v);
    });

    registry.register('ASINH', (...args: FormulaValue[]) => Math.asinh(toNum(args[0])));

    registry.register('ATAN', (...args: FormulaValue[]) => Math.atan(toNum(args[0])));

    registry.register('ATAN2', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const y = toNum(args[1]);
        if (x === 0 && y === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'ATAN2: both arguments are zero');
        return Math.atan2(y, x);
    });

    registry.register('ATANH', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v <= -1 || v >= 1) throw new FormulaError(FormulaErrorType.NUM, 'ATANH argument must be in (-1, 1)');
        return Math.atanh(v);
    });

    registry.register('COS', (...args: FormulaValue[]) => Math.cos(toNum(args[0])));
    registry.register('COSH', (...args: FormulaValue[]) => Math.cosh(toNum(args[0])));

    registry.register('COT', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        const s = Math.sin(v);
        if (s === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'COT: division by zero');
        return Math.cos(v) / s;
    });

    registry.register('COTH', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'COTH: division by zero');
        return Math.cosh(v) / Math.sinh(v);
    });

    registry.register('CSC', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        const s = Math.sin(v);
        if (s === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'CSC: division by zero');
        return 1 / s;
    });

    registry.register('CSCH', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'CSCH: division by zero');
        return 1 / Math.sinh(v);
    });

    registry.register('SEC', (...args: FormulaValue[]) => {
        return 1 / Math.cos(toNum(args[0]));
    });

    registry.register('SECH', (...args: FormulaValue[]) => {
        return 1 / Math.cosh(toNum(args[0]));
    });

    registry.register('SIN', (...args: FormulaValue[]) => Math.sin(toNum(args[0])));
    registry.register('SINH', (...args: FormulaValue[]) => Math.sinh(toNum(args[0])));
    registry.register('TAN', (...args: FormulaValue[]) => Math.tan(toNum(args[0])));
    registry.register('TANH', (...args: FormulaValue[]) => Math.tanh(toNum(args[0])));

    // --- Angle conversion ---

    registry.register('DEGREES', (...args: FormulaValue[]) => toNum(args[0]) * (180 / Math.PI));
    registry.register('RADIANS', (...args: FormulaValue[]) => toNum(args[0]) * (Math.PI / 180));
    registry.register('PI', () => Math.PI);

    // --- Rounding ---

    registry.register('ROUND', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const digits = toNum(args[1]);
        const factor = Math.pow(10, digits);
        return Math.round(number * factor) / factor;
    });

    registry.register('ROUNDDOWN', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const digits = toNum(args[1]);
        const factor = Math.pow(10, digits);
        return (number >= 0 ? Math.floor : Math.ceil)(number * factor) / factor;
    });

    registry.register('ROUNDUP', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const digits = toNum(args[1]);
        const factor = Math.pow(10, digits);
        return (number >= 0 ? Math.ceil : Math.floor)(number * factor) / factor;
    });

    registry.register('TRUNC', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const digits = args[1] !== undefined ? toNum(args[1]) : 0;
        const factor = Math.pow(10, digits);
        return Math.trunc(number * factor) / factor;
    });

    registry.register('INT', (...args: FormulaValue[]) => Math.floor(toNum(args[0])));

    registry.register('EVEN', (...args: FormulaValue[]) => {
        const n = toNum(args[0]);
        const ceiled = Math.ceil(Math.abs(n));
        const even = ceiled % 2 === 0 ? ceiled : ceiled + 1;
        return n < 0 ? -even : even;
    });

    registry.register('ODD', (...args: FormulaValue[]) => {
        const n = toNum(args[0]);
        const ceiled = Math.ceil(Math.abs(n));
        const odd = ceiled % 2 !== 0 ? ceiled : ceiled + 1;
        return n < 0 ? -odd : odd;
    });

    registry.register('MROUND', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const multiple = toNum(args[1]);
        if (multiple === 0) return 0;
        if ((number < 0 && multiple > 0) || (number > 0 && multiple < 0)) {
            throw new FormulaError(FormulaErrorType.NUM, 'MROUND: number and multiple must have the same sign');
        }
        return smartRound(Math.round(number / multiple) * multiple);
    });

    registry.register('CEILING', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = toNum(args[1]);
        if (significance === 0) return 0;
        if (number > 0 && significance < 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'CEILING: if number is positive, significance must be positive');
        }
        return smartRound(Math.ceil(number / significance) * significance);
    });

    registry.register('CEILING.MATH', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = args[1] !== undefined ? Math.abs(toNum(args[1])) : 1;
        const mode = args[2] !== undefined ? toNum(args[2]) : 0;
        if (significance === 0) return 0;
        if (number < 0 && mode !== 0) {
            // mode != 0 means round toward zero (i.e. floor towards zero = ceil in absolute terms)
            return smartRound(-Math.floor(Math.abs(number) / significance) * significance);
        }
        return smartRound(Math.ceil(number / significance) * significance);
    });

    registry.register('CEILING.PRECISE', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = args[1] !== undefined ? Math.abs(toNum(args[1])) : 1;
        if (significance === 0) return 0;
        return smartRound(Math.ceil(number / significance) * significance);
    });

    registry.register('ISO.CEILING', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = args[1] !== undefined ? Math.abs(toNum(args[1])) : 1;
        if (significance === 0) return 0;
        return smartRound(Math.ceil(number / significance) * significance);
    });

    registry.register('FLOOR', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = toNum(args[1]);
        if (significance === 0) return 0;
        if (number > 0 && significance < 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'FLOOR: if number is positive, significance must be positive');
        }
        return smartRound(Math.floor(number / significance) * significance);
    });

    registry.register('FLOOR.MATH', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = args[1] !== undefined ? Math.abs(toNum(args[1])) : 1;
        const mode = args[2] !== undefined ? toNum(args[2]) : 0;
        if (significance === 0) return 0;
        if (number < 0 && mode !== 0) {
            // mode != 0: round away from zero
            return smartRound(-Math.ceil(Math.abs(number) / significance) * significance);
        }
        return smartRound(Math.floor(number / significance) * significance);
    });

    registry.register('FLOOR.PRECISE', (...args: FormulaValue[]) => {
        const number = toNum(args[0]);
        const significance = args[1] !== undefined ? Math.abs(toNum(args[1])) : 1;
        if (significance === 0) return 0;
        return smartRound(Math.floor(number / significance) * significance);
    });

    // --- Arithmetic ---

    registry.register('SUM', (...args: FormulaValue[]) =>
        smartRound(flattenArgs(args).reduce((a, b) => a + b, 0))
    );

    registry.register('PRODUCT', (...args: FormulaValue[]) =>
        smartRound(flattenArgs(args).reduce((a, b) => smartRound(a * b), 1))
    );

    registry.register('SUMSQ', (...args: FormulaValue[]) =>
        smartRound(flattenArgs(args).reduce((a, b) => a + b * b, 0))
    );

    registry.register('SUMPRODUCT', (...args: FormulaValue[]) => {
        // If there is only one array argument, just sum it
        if (args.length === 1) {
            const arr = Array.isArray(args[0]) ? args[0] : [toNum(args[0])];
            return smartRound(arr.reduce((a, b) => a + b, 0));
        }
        // Multiple arrays: element-wise multiply then sum
        const arrays = args.map(a => Array.isArray(a) ? a : [toNum(a)]);
        const len = Math.min(...arrays.map(a => a.length));
        let sum = 0;
        for (let i = 0; i < len; i++) {
            let product = 1;
            for (const arr of arrays) {
                product *= arr[i];
            }
            sum += product;
        }
        return smartRound(sum);
    });

    registry.register('SUMX2MY2', (...args: FormulaValue[]) => {
        const xArr = Array.isArray(args[0]) ? args[0] : [toNum(args[0])];
        const yArr = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const len = Math.min(xArr.length, yArr.length);
        let sum = 0;
        for (let i = 0; i < len; i++) sum += xArr[i] ** 2 - yArr[i] ** 2;
        return smartRound(sum);
    });

    registry.register('SUMX2PY2', (...args: FormulaValue[]) => {
        const xArr = Array.isArray(args[0]) ? args[0] : [toNum(args[0])];
        const yArr = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const len = Math.min(xArr.length, yArr.length);
        let sum = 0;
        for (let i = 0; i < len; i++) sum += xArr[i] ** 2 + yArr[i] ** 2;
        return smartRound(sum);
    });

    registry.register('SUMXMY2', (...args: FormulaValue[]) => {
        const xArr = Array.isArray(args[0]) ? args[0] : [toNum(args[0])];
        const yArr = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const len = Math.min(xArr.length, yArr.length);
        let sum = 0;
        for (let i = 0; i < len; i++) sum += (xArr[i] - yArr[i]) ** 2;
        return smartRound(sum);
    });

    registry.register('MOD', (...args: FormulaValue[]) => {
        const n = toNum(args[0]);
        const d = toNum(args[1]);
        if (d === 0) return 0;
        // Excel MOD: sign follows divisor
        return smartRound(n - d * Math.floor(n / d));
    });

    registry.register('QUOTIENT', (...args: FormulaValue[]) => {
        const n = toNum(args[0]);
        const d = toNum(args[1]);
        if (d === 0) return 0;
        return Math.trunc(n / d);
    });

    registry.register('SIGN', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v > 0) return 1;
        if (v < 0) return -1;
        return 0;
    });

    registry.register('SQRT', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v < 0) throw new FormulaError(FormulaErrorType.NUM, 'SQRT: argument must be non-negative');
        return Math.sqrt(v);
    });

    registry.register('SQRTPI', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v < 0) throw new FormulaError(FormulaErrorType.NUM, 'SQRTPI: argument must be non-negative');
        return Math.sqrt(v * Math.PI);
    });

    registry.register('POWER', (...args: FormulaValue[]) => {
        const base = toNum(args[0]);
        const exp = toNum(args[1]);
        return smartRound(Math.pow(base, exp));
    });

    registry.register('EXP', (...args: FormulaValue[]) => Math.exp(toNum(args[0])));

    registry.register('LN', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v <= 0) throw new FormulaError(FormulaErrorType.NUM, 'LN: argument must be positive');
        return Math.log(v);
    });

    registry.register('LOG', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        const base = args[1] !== undefined ? toNum(args[1]) : 10;
        if (v <= 0) throw new FormulaError(FormulaErrorType.NUM, 'LOG: argument must be positive');
        if (base <= 0 || base === 1) throw new FormulaError(FormulaErrorType.NUM, 'LOG: base must be positive and not 1');
        return Math.log(v) / Math.log(base);
    });

    registry.register('LOG10', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (v <= 0) throw new FormulaError(FormulaErrorType.NUM, 'LOG10: argument must be positive');
        return Math.log10(v);
    });

    registry.register('RAND', () => Math.random());

    registry.register('RANDBETWEEN', (...args: FormulaValue[]) => {
        const low = Math.ceil(toNum(args[0]));
        const high = Math.floor(toNum(args[1]));
        if (low > high) throw new FormulaError(FormulaErrorType.NUM, 'RANDBETWEEN: bottom must be <= top');
        return Math.floor(Math.random() * (high - low + 1)) + low;
    });

    // --- Factorial / Combinatorics ---

    registry.register('FACT', (...args: FormulaValue[]) => {
        const n = Math.floor(toNum(args[0]));
        return factorial(n);
    });

    registry.register('FACTDOUBLE', (...args: FormulaValue[]) => {
        const n = Math.floor(toNum(args[0]));
        return factorialDouble(n);
    });

    registry.register('COMBIN', (...args: FormulaValue[]) => {
        const n = Math.floor(toNum(args[0]));
        const k = Math.floor(toNum(args[1]));
        if (n < 0 || k < 0 || k > n) throw new FormulaError(FormulaErrorType.NUM, 'COMBIN: invalid arguments');
        return factorial(n) / (factorial(k) * factorial(n - k));
    });

    registry.register('COMBINA', (...args: FormulaValue[]) => {
        const n = Math.floor(toNum(args[0]));
        const k = Math.floor(toNum(args[1]));
        if (n < 0 || k < 0) throw new FormulaError(FormulaErrorType.NUM, 'COMBINA: invalid arguments');
        if (n === 0 && k === 0) return 1;
        return factorial(n + k - 1) / (factorial(k) * factorial(n - 1));
    });

    registry.register('MULTINOMIAL', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args).map(v => Math.floor(v));
        const total = nums.reduce((a, b) => a + b, 0);
        const numerator = factorial(total);
        const denominator = nums.reduce((a, b) => a * factorial(b), 1);
        return numerator / denominator;
    });

    // --- GCD / LCM ---

    registry.register('GCD', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) return 0;
        return nums.reduce((a, b) => gcd2(a, b));
    });

    registry.register('LCM', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) return 0;
        return nums.reduce((a, b) => lcm2(a, b));
    });

    // --- Number base conversion ---

    registry.register('BASE', (...args: FormulaValue[]) => {
        const number = Math.floor(toNum(args[0]));
        const radix = Math.floor(toNum(args[1]));
        const minLength = args[2] !== undefined ? Math.floor(toNum(args[2])) : 0;
        if (radix < 2 || radix > 36) throw new FormulaError(FormulaErrorType.NUM, 'BASE: radix must be between 2 and 36');
        if (number < 0) throw new FormulaError(FormulaErrorType.NUM, 'BASE: number must be non-negative');
        const result = number.toString(radix).toUpperCase();
        return result.padStart(minLength, '0');
    });

    registry.register('DECIMAL', (...args: FormulaValue[]) => {
        const text = toStr(args[0]);
        const radix = Math.floor(toNum(args[1]));
        if (radix < 2 || radix > 36) throw new FormulaError(FormulaErrorType.NUM, 'DECIMAL: radix must be between 2 and 36');
        const result = parseInt(text, radix);
        if (isNaN(result)) throw new FormulaError(FormulaErrorType.VALUE, `DECIMAL: cannot parse "${text}" in base ${radix}`);
        return result;
    });

    // --- Roman numerals ---

    registry.register('ROMAN', (...args: FormulaValue[]) => {
        const n = Math.floor(toNum(args[0]));
        return toRoman(n);
    });

    registry.register('ARABIC', (...args: FormulaValue[]) => {
        const text = toStr(args[0]).trim();
        if (text === '') return 0;
        return fromRoman(text);
    });

    // --- Series / advanced ---

    registry.register('SERIESSUM', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const n = toNum(args[1]);
        const m = toNum(args[2]);
        const coefficients = Array.isArray(args[3]) ? args[3] : [toNum(args[3])];
        let sum = 0;
        for (let i = 0; i < coefficients.length; i++) {
            sum += coefficients[i] * Math.pow(x, n + i * m);
        }
        return smartRound(sum);
    });

    registry.register('SUBTOTAL', (...args: FormulaValue[]) => {
        if (args.length < 2) throw new FormulaError(FormulaErrorType.VALUE, 'SUBTOTAL requires at least 2 arguments');
        let funcNum = Math.floor(toNum(args[0]));
        // Excel also allows 101–111 (ignoring hidden rows); treat them the same
        if (funcNum > 100) funcNum -= 100;
        const nums = flattenArgs(args.slice(1));
        return subtotalOp(funcNum, nums);
    });
}
